import type { DeckConfig } from '../types/deck'
import { TAROT_DECK } from './tarot-deck'

// Image prefixes for oracle decks point at the auth-gated API endpoint
// (`${VITE_API_URL}/api/decks/:id/cards/...`); resolved on the frontend at
// fetch time. Public/static decks use `/cards/...` directly.
export const DECK_REGISTRY: DeckConfig[] = [
  {
    id: 'cosmic-finder',
    name: 'The Cosmic Finder',
    description: 'Intuitive readings guided by warmth, wisdom, and the stars',
    cardBackPath: '/cards/card-back.webp',
    cardImagePrefix: '/cards/',
    cardKind: 'tarot',
    visibility: 'public',
    cards: TAROT_DECK,
    limits: { type: 'unlimited' },
  },
  {
    id: 'breath-of-gold',
    name: 'Breath of Gold',
    description:
      'A 67-card oracle deck inviting you to breathe in a virtue. Art and guidance by Adrienne Rivera.',
    // Image prefix is resolved at runtime against VITE_API_URL when the
    // deck is selected. Stored without API host so it stays portable.
    cardBackPath: '/api/decks/breath-of-gold/cards/card-back.webp',
    cardImagePrefix: '/api/decks/breath-of-gold/cards/',
    cardKind: 'oracle',
    // Public = released/visible to everyone; the `cosmicOnly` flag below is what
    // gates USE to paying Cosmic members (free users see it locked → upgrade).
    visibility: 'public',
    cosmicOnly: true,
    cardCount: 67,
    // cards: undefined — frontend fetches from /api/decks/breath-of-gold on selection
    limits: { type: 'unlimited' },
    creatorCredit: 'by Adrienne Rivera',
    artist: 'Adrienne Rivera',
    // `beingwhoyouwant.com` is a parked GoDaddy domain (it JS-redirects to a
    // domain-parking lander). Use the in-app practitioner page via creatorSlug
    // instead of an external link — `artistWebsite` left unset.
    creatorSlug: 'adrienne',
  },
  {
    id: 'la-caribena-oracle',
    name: 'La Caribeña Oracle',
    description:
      'A 50-card oracle deck of original paintings channeling the wisdom, beauty, and resilience of the Caribbean. Art and guidance by Jasleni Brito.',
    cardBackPath: '/api/decks/la-caribena-oracle/cards/card-back.webp',
    cardImagePrefix: '/api/decks/la-caribena-oracle/cards/',
    cardKind: 'oracle',
    // Admin-only preview until launch day. The `la_caribena_live` feature flag
    // flips effective visibility to 'public' when an admin toggles it on — no
    // redeploy. `cosmicOnly` then gates draws to paying Cosmic members (free
    // users see it locked with an upgrade prompt). See effectiveDeckVisibility().
    visibility: 'admin',
    cosmicOnly: true,
    releaseFlag: 'la_caribena_live',
    // Let Jasleni preview + test her own deck before launch, scoped to JUST her
    // account email — without opening it to other beta testers or users. No
    // effect once the flag flips it public. See isDeckPreviewer().
    previewEmails: ['info@jaslenidesigns.com'],
    // Jasleni's paintings are a standard oracle-card ratio (2250 × 3300 ≈ 0.682),
    // squarer than the default tarot frame — set explicitly so the 3D card
    // geometry matches her art instead of squishing it.
    aspectRatio: 2250 / 3300,
    cardCount: 50,
    // cards: undefined — frontend fetches from /api/decks/la-caribena-oracle on selection
    limits: { type: 'unlimited' },
    creatorCredit: 'by Jasleni Brito',
    artist: 'Jasleni Brito',
    creatorSlug: 'jasleni',
  },
  {
    id: 'four-weathers',
    name: 'Four Weathers',
    description:
      'A 44-card oracle deck of weather, season and catastrophe — fire, water, earth, air. Each card carries a line, a reading, and one step to take. Written by Sarah.',
    cardBackPath: '/api/decks/four-weathers/cards/card-back.webp',
    cardImagePrefix: '/api/decks/four-weathers/cards/',
    cardKind: 'oracle',
    // Admin-only preview until launch. Flipping `four_weathers_live` in
    // Admin → Flags makes it public with no redeploy; `cosmicOnly` then gates
    // drawing to paying members. See effectiveDeckVisibility().
    visibility: 'admin',
    cosmicOnly: true,
    releaseFlag: 'four_weathers_live',
    // No previewEmails — Sarah wrote this deck and is an admin, so
    // visibility:'admin' already covers her preview. (Jasleni needed an entry
    // because she is not staff.)
    //
    // Same standard oracle ratio as La Caribeña (2250 × 3300 ≈ 0.682), so the
    // art drops straight into the existing 3D card geometry.
    aspectRatio: 2250 / 3300,
    cardCount: 44,
    // cards: undefined — frontend fetches from /api/decks/four-weathers on selection
    limits: { type: 'unlimited' },
    creatorCredit: 'by Sarah',
    artist: 'Sarah',
    creatorSlug: 'sarah',
  },
]

export const DEFAULT_DECK = DECK_REGISTRY[0]
